/**
 * @file まなぶ人の計画に入っている本 (ISBN) と、その本の今週の範囲を読む hook。
 *
 * @what: 学ぶ人が取り込んだ学びの計画と家族の参考書ルートを読み、進めている計画のカリキュラムに載って
 *   いる本の ISBN (model/shelfSections.ts の plannedBookIsbns) と、スケジュールに入った本ごとの今週の範囲
 *   (model/plannedRanges.ts) を返す。本だなが「取り組むもの」と「教養」を分け、取り組む本のバーに今週の
 *   位置を置き、リーダーの目次に「今週の範囲」を出すのに使う。
 *   あわせて、本 (ISBN) が載っているカリキュラムの短い名前 (model/curriculumTags.ts) も返す。こちらは
 *   取り込んだ計画で絞らず、家族にあるカリキュラム全部を対象にする (代表の決定、2026-09-16)。本だなの
 *   カードのタグに使う。
 * @why: 取り込んだときの役割は家族で1つしか持てないので、人ごとの見え方は計画から出す
 *   (model/shelfSections.ts)。計画を取り込んでいない人では空になり、本だなは「教養」だけになる。
 *   区画はカリキュラムに載っているかで決め (代表の判断、2026-09-13)、今週の範囲はスケジュールに入った本
 *   だけが持つ (代替・補助の本は区画に入るが範囲は持たない)。
 * @failure: まなぶ人が未定のうちは `idle` を返し、計画を読まない (呼び手は取り込んだときの役割で並べる)。
 *   カリキュラムの名前はまなぶ人が未定でも返す。計画かルートの取得に失敗したら `error` を返す (呼び手は
 *   取り込んだときの役割へ倒し、1行で知らせる)。
 */

import { useMemo } from "react";
import { todayLocal } from "../api/dates";
import { curriculumNamesByIsbn } from "../model/curriculumTags";
import { plannedRangesOf, schedulesOfPlans, type PlannedBookRange } from "../model/plannedRanges";
import { plannedBookIsbns } from "../model/shelfSections";
import { useLearningPlans } from "./useLearningPlans";
import { useTextbookRoutes } from "./useTextbookRoutes";

export type PlannedBooksState = (
  | { status: "idle" }
  | { status: "loading" }
  | {
      status: "ready";
      isbns: ReadonlySet<string>;
      /** 本 (ISBN) → 今週の範囲である。呼び手が渡さないときは無い (テストの都合)。 */
      ranges?: ReadonlyMap<string, PlannedBookRange>;
    }
  | { status: "error" }
) & {
  /**
   * 本 (ISBN) → その本が載っているカリキュラムの短い名前である。ルートを読めるまでと、読めなかった
   * ときは無い。取り込んだ計画では絞らない。
   */
  curriculums?: ReadonlyMap<string, readonly string[]>;
};

export function usePlannedBooks(
  familyId: string,
  learnerPersonId: string | null,
  /** 本だなの面を開いている間だけ読む (ほかの面でルートの入れ子を持ち回らない)。 */
  active: boolean,
): PlannedBooksState {
  const enabled = active && learnerPersonId !== null;
  const planState = useLearningPlans(familyId, learnerPersonId, enabled);
  // ルートはまなぶ人が未定でも読む。※なぜならカリキュラムのタグは家族にあるカリキュラム全部を
  // 対象にし、誰を選んでいるかで変わらないためである。
  const routesState = useTextbookRoutes(familyId, active);
  const plans = planState.state.status === "ready" ? planState.state.plans : null;
  const routes = routesState.status === "ready" ? routesState.routes : null;

  return useMemo<PlannedBooksState>(() => {
    const curriculums = routes === null ? undefined : curriculumNamesByIsbn(routes);
    const tag = curriculums === undefined ? {} : { curriculums };
    if (!enabled) return { status: "idle", ...tag };
    if (planState.state.status === "error" || routesState.status === "error")
      return { status: "error", ...tag };
    if (routes === null || plans === null) return { status: "loading", ...tag };
    const today = todayLocal();
    return {
      status: "ready",
      isbns: plannedBookIsbns(routes, plans),
      ranges: plannedRangesOf(schedulesOfPlans(routes, plans, today), today),
      ...tag,
    };
  }, [enabled, planState.state.status, routesState.status, routes, plans]);
}
